import React, { useEffect, useRef, useState } from 'react'

import { AxiosError } from 'axios'
import {
  Button,
  CheckBox,
  Col,
  Input,
  Picker,
  Row,
  Span,
  InputMask,
  AutoCompleteInput,
  Grid
} from 'components'
import { useModal, useLoading, useDebounce } from 'hooks'
import Masker from 'hooks/useMasker'
import moment from 'moment'
import { ContractServices, ResponseContractById } from 'services/NGT/Contract'
import {
  CaixaServices,
  ICaixaResponseDto,
  DocumentoCaixaServices,
  TipoCaixa,
  ICaixaForm,
  ICaixaModel
} from 'services/NGT/Files'
import { FONTS, SPACING, TYPOGRAPHY, FORMAT } from 'utils'

const DEFAULT_VALUES_ICAIXAFORM: ICaixaForm = {
  caixaDepartamental: {
    idCaixa: 0,
    idTipoCaixa: TipoCaixa.Departamental,
    numeroCaixa: ''
  },
  caixaExterna: {
    idCaixa: 0,
    idTipoCaixa: TipoCaixa.Departamental,
    numeroCaixa: ''
  },
  cdContrato: '',
  cnpj: '',
  competencia: moment(),
  currentCaixa: TipoCaixa.Externa,
  descricao: '',
  nmCentro: '',
  nmEmpresa: '',
  nmFornecedor: ''
}

interface IAddNewDocumentation {
  handleChange: (_: any, _newValue: any) => void
}

interface ICaixa {
  externa: ICaixaResponseDto
  departamental: ICaixaResponseDto
}

const AddNewDocumentation: React.FC<IAddNewDocumentation> = ({
  handleChange
}) => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [caixa, setCaixa] = useState<ICaixa>({
    departamental: {
      flCaixaAtual: false,
      idCaixa: 0,
      idCentroPlanta: 0,
      idTipoCaixa: TipoCaixa.Departamental,
      nuAnoMesCompetencia: '',
      nuCaixa: 0
    },
    externa: {
      flCaixaAtual: false,
      idCaixa: 0,
      idCentroPlanta: 0,
      idTipoCaixa: TipoCaixa.Externa,
      nuAnoMesCompetencia: '',
      nuCaixa: 0
    }
  })
  const [contract, setContract] = useState<ResponseContractById | undefined>()
  const [contratos, setContratos] = useState([])
  const [selectedContrato, setSelectedContrato] = useState('')
  const [form, setForm] = useState<ICaixaForm>(DEFAULT_VALUES_ICAIXAFORM)
  const incrementValue = useRef<number>(0)

  const currentCaixa = (): ICaixaModel =>
    form.currentCaixa == TipoCaixa.Externa
      ? form.caixaExterna
      : form.caixaDepartamental

  const onSubmit = () => {
    if (!contract && form.currentCaixa === TipoCaixa.Departamental) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar o contrato',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    const caixaAtual = currentCaixa()

    setLoading('loading')

    DocumentoCaixaServices()
      .create({
        idCaixa: caixaAtual.idCaixa,
        idCentroPlanta: contract.centroPlantaId,
        idContrato: contract.idContrato,
        idDocumentoCaixa: 0,
        isCaixaExterna: form.currentCaixa === TipoCaixa.Externa,
        nuAnoMesCompetencia: moment(form.competencia).format('yyyyMM'),
        nuCaixa: Number(currentCaixa().numeroCaixa),
        txDescricaoCaixa: form.descricao || ''
      })
      .then((response) => {
        if (response.status === 200) {
          clearForm()
          showModal({
            hideButtonCancel: true,
            message: 'Dados incluídos com sucesso',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
        }

        setLoading('idle')
        handleChange(null, 0)
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível salvar o documento',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleGetContractError = () =>
    showModal({
      hideButtonCancel: true,
      message: `Não foi possível obter os dados do contrato`,
      txtButtonConfirm: 'OK',
      type: 'error'
    })

  const handleCaixaError = () =>
    showModal({
      hideButtonCancel: true,
      message: 'Não foi possível obter os dados da caixa',
      txtButtonConfirm: 'OK',
      type: 'error'
    })

  const clearForm = () => {
    setContract(undefined)
    setSelectedContrato(undefined)
    setForm((prevState) => ({
      ...DEFAULT_VALUES_ICAIXAFORM,
      caixaExterna: {
        ...prevState.caixaExterna
      }
    }))
  }

  const getContractByCd = (cdContract: string) => {
    if (cdContract) {
      setLoading('loading')
      ContractServices()
        .GetByCodigo(cdContract)
        .then((response) => {
          if (response.status === 200) {
            const { data: contract } = response
            setContract(contract)
            setForm((prevState) => ({
              ...prevState,
              cdContrato: String(contract.cdContrato),
              cnpj: Masker(
                contract?.cnpj?.trim(),
                isNaN(parseInt(contract?.cnpj))
                  ? ['NN###########']
                  : ['###.###.###-##', '##.###.###/####-##']
              ),
              currentCaixa: TipoCaixa.Departamental,
              nmCentro: contract?.nmCentro?.trim(),
              nmEmpresa: contract?.nmEmpresa?.trim(),
              nmFornecedor: contract?.nmFornecedor?.trim()
            }))
          } else {
            setContract(undefined)
            clearForm()
            setLoading('idle')
            handleGetContractError()
          }

          setLoading('idle')
        })
        .catch(() => {
          setContract(undefined)
          setLoading('idle')
          handleGetContractError()
        })
    } else {
      setContract(undefined)
      clearForm()
    }
  }

  const handleSelectCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tipoCaixa: TipoCaixa =
      event.target.name === 'caixaDepartamental'
        ? TipoCaixa.Departamental
        : TipoCaixa.Externa

    if (event.target.name === 'caixaDepartamental' && !contract) {
      showModal({
        hideButtonCancel: true,
        message:
          'Antes de marcar a caixa departamental você precisa buscar por um contrato',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    setForm((prevState) => ({
      ...prevState,
      currentCaixa: tipoCaixa
    }))
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line prefer-const
    let [section, key] = event.target.name.split('.')

    if (section === 'currentCaixa' && key) {
      section =
        form.currentCaixa === TipoCaixa.Externa
          ? 'caixaExterna'
          : 'caixaDepartamental'
    }

    if (key) {
      setForm((prevState) => ({
        ...prevState,
        [section]: {
          ...prevState[section],
          [key]: event.target.value
        }
      }))
    } else {
      setForm((prevState) => ({
        ...prevState,
        [event.target.name]: event.target.value
      }))
    }
  }

  const validadeCaixaDepartamentalForm = (tipoCaixa: TipoCaixa) => {
    const isValid = true
    const isNotValid = false

    const caixaSelecionada: ICaixaModel =
      tipoCaixa === TipoCaixa.Externa
        ? form.caixaExterna
        : form.caixaDepartamental

    if (!contract?.idContrato && tipoCaixa === TipoCaixa.Departamental) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar o contrato',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return isNotValid
    }

    if (!form?.competencia) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar a competência',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return isNotValid
    }

    if (!caixaSelecionada?.numeroCaixa) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar o número da caixa',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return isNotValid
    }

    return isValid
  }

  const incrementCaixa = (idTipoCaixa: TipoCaixa, isAbrirCaixa?: boolean) => {
    setLoading('loading')

    const caixaAtualSelecionada = currentCaixa()

    CaixaServices()
      .create({
        flCaixaAtual: false,
        idCentroPlanta: contract?.centroPlantaId,
        idTipoCaixa: idTipoCaixa,
        isAbrirCaixa: isAbrirCaixa,
        nuCaixa:
          Number(caixaAtualSelecionada.numeroCaixa) + incrementValue.current
      })
      .then(({ data, status }) => {
        const { valor } = data

        if (status === 200 && valor) {
          setForm((prevState) => ({
            ...prevState,
            [form.currentCaixa === TipoCaixa.Externa
              ? 'caixaExterna'
              : 'caixaDepartamental']: {
              idCaixa: valor.idCaixa,
              idTipoCaixa: valor.idTipoCaixa,
              numeroCaixa: valor.nuCaixa
            }
          }))

          setCaixa((oldData) => ({
            ...oldData,
            [idTipoCaixa === TipoCaixa.Externa ? 'externa' : 'departamental']:
              valor
          }))

          setLoading('idle')
          incrementValue.current = 0
        }
      })
      .catch((error) => {
        const axiosError = error as AxiosError
        incrementValue.current = 0
        setLoading('idle')

        const responseError = axiosError.response?.data.detail
          ? error.response.data.detail
          : ' '

        showModal({
          hideButtonCancel: true,
          message: `Não foi possível gerar a caixa. ${responseError}`,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const gerarCaixaExterno = () => {
    if (!contract && form.currentCaixa === TipoCaixa.Departamental) {
      showModal({
        hideButtonCancel: true,
        message: 'Primeiro você deve digitar o número do contrato.',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    if (!validadeCaixaDepartamentalForm(form.currentCaixa)) {
      return
    }

    if (
      caixa?.externa.nuCaixa + 1 < Number(form.caixaExterna.numeroCaixa) ||
      caixa?.externa.nuCaixa > Number(form.caixaExterna.numeroCaixa)
    ) {
      showModalAbrirCaixa(TipoCaixa.Externa)
      return
    }

    if (caixa?.externa.nuCaixa === Number(form.caixaExterna.numeroCaixa)) {
      setForm((prevState: ICaixaForm) => ({
        ...prevState,
        caixaExterna: {
          ...prevState.caixaExterna,
          numeroCaixa: (caixa?.externa.nuCaixa + 1).toString()
        }
      }))
      incrementValue.current = 1
    }

    incrementCaixa(TipoCaixa.Externa, true)
  }

  const showModalAbrirCaixa = (tipoCaixa: TipoCaixa) => {
    showModal({
      handleCancel: () => incrementCaixa(tipoCaixa, false),
      handleConfirm: () => incrementCaixa(tipoCaixa, true),
      message: 'Devo considerar nova numeração como próxima sequência (S/N)?',
      txtButtonCancel: 'NÃO',
      txtButtonConfirm: 'SIM',
      type: 'alert'
    })
  }

  const gerarCaixaDepartamental = () => {
    if (!validadeCaixaDepartamentalForm(form.currentCaixa)) {
      return
    }

    if (!caixa) {
      incrementCaixa(2, false)
      return
    }

    if (
      caixa?.departamental.nuCaixa + 1 <
      Number(form.caixaDepartamental.numeroCaixa) ||
      caixa?.departamental.nuCaixa > Number(form.caixaDepartamental.numeroCaixa)
    ) {
      showModalAbrirCaixa(TipoCaixa.Departamental)
      return
    }

    if (
      caixa?.departamental.nuCaixa ===
      Number(form.caixaDepartamental.numeroCaixa)
    ) {
      setForm((prevState: ICaixaForm) => ({
        ...prevState,
        caixaDepartamental: {
          ...prevState.caixaDepartamental,
          numeroCaixa: (caixa?.departamental.nuCaixa + 1).toString()
        }
      }))
      incrementValue.current = 1
    }

    incrementCaixa(TipoCaixa.Departamental, true)
  }

  const getCaixa = (idCentroPlanta?: number) => {
    setLoading('loading')

    CaixaServices()
      .findAtual(idCentroPlanta)
      .then((response) => {
        if (response.status === 200) {
          const { data: caixa } = response

          if (caixa) {
            setCaixa((oldData) => ({
              ...oldData,
              [idCentroPlanta ? 'departamental' : 'externa']: caixa
            }))
          }

          setForm((prevState) => ({
            ...prevState,
            [caixa.idTipoCaixa === TipoCaixa.Externa
              ? 'caixaExterna'
              : 'caixaDepartamental']: {
              idCaixa: caixa.idCaixa,
              idTipoCaixa: caixa.idTipoCaixa,
              numeroCaixa: caixa.nuCaixa
            }
          }))
        } else if (response.status === 204) {
          setForm((prevState) => ({
            ...prevState,
            caixaDepartamental: {
              idCaixa: 0,
              idTipoCaixa: TipoCaixa.Departamental,
              numeroCaixa: ''
            },
            caixaExterna: {
              idCaixa: 0,
              idTipoCaixa: TipoCaixa.Externa,
              numeroCaixa: ''
            },
            currentCaixa: TipoCaixa.Externa
          }))
        } else {
          handleCaixaError()
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        handleCaixaError()
      })
  }

  const handleSearchContract = (event) => {
    setLoading('loading')
    ContractServices()
      .GetContractByContrato(event.target.value)
      .then((response) => {
        const data = response.data.entity.map((contrato) => ({
          label: contrato.cdContrato,
          value: contrato.cdContrato
        }))
        setContratos(data)
        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao obter os dados do contrato',
          txtButtonConfirm: 'OK',
          type: 'alert'
        })
      })
  }

  useEffect(() => {
    getCaixa()
  }, [])

  useEffect(() => {
    getContractByCd(selectedContrato)
  }, [selectedContrato])

  useEffect(() => {
    if (contract) {
      getCaixa(contract?.centroPlantaId)
    }
  }, [contract])

  return (
    <>
      <Grid
        gridCols="1.2fr 1.9fr 2fr 0.9fr 2fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`${SPACING.spacing15} 0 ${SPACING.spacing15} 0`}
      >
        <AutoCompleteInput
          marginTop={SPACING.spacing5}
          name="contrato"
          noFilter
          onChangeText={useDebounce(handleSearchContract, 1000)}
          value={selectedContrato || null}
          options={contratos || []}
          optionValue="label"
          label="Contrato"
          onChange={(selectedValue) => {
            setSelectedContrato(selectedValue)
          }}
          valueName="contrato"
        />
        <Input
          value={form.nmEmpresa}
          label="Empresa"
          placeholder="Empresa"
          disabled
        />
        <Input
          label="Centro"
          value={form.nmCentro}
          placeholder="Centro"
          disabled
        />
        <Input
          label="CPF/CNPJ/Cód.Fornecedor"
          value={form.cnpj && FORMAT.formatCdDocto(form.cnpj)}
          placeholder="Nº"
          disabled
        />
        <Input
          label="Fornecedor"
          value={form.nmFornecedor}
          placeholder="Fornecedor"
          disabled
        />
      </Grid>
      <Span
        text="ARQUIVO"
        marginTop={SPACING.spacing20}
        fontWeight={FONTS.bold}
        fontSize={TYPOGRAPHY.bodySize16}
      />

      <Row alignItems="center" marginTop={SPACING.spacing20}>
        <Picker
          label="Competência"
          views={['year', 'month']}
          marginRight={SPACING.spacing15}
          value={form.competencia}
          onChange={(date) =>
            setForm((prevState) => ({
              ...prevState,
              competencia: date
            }))
          }
          width="150px"
          type="monthAndYear"
        />
        <Col>
          <Span text="Caixa Externo" />
          <CheckBox
            checked={form.currentCaixa === TipoCaixa.Externa}
            name="caixaExterno"
            onChange={handleSelectCheckbox}
          />
        </Col>
        <InputMask
          marginTop={SPACING.spacing20}
          marginLeft="-3rem"
          disabled={form.currentCaixa === TipoCaixa.Departamental}
          value={form.caixaExterna.numeroCaixa}
          formValue="caixaExterna.numeroCaixa"
          name="caixaExterna.numeroCaixa"
          onChange={handleOnChange}
          type="number"
          maxLength={8}
          onBlur={
            caixa?.externa?.nuCaixa + 1 <
              Number(form.caixaExterna.numeroCaixa) ||
              caixa?.externa?.nuCaixa > Number(form.caixaExterna.numeroCaixa)
              ? gerarCaixaExterno
              : null
          }
        />
        <Button
          text="GERAR CAIXA"
          disabled={form.currentCaixa === TipoCaixa.Departamental}
          marginLeft={SPACING.spacing10}
          onClick={gerarCaixaExterno}
        />
      </Row>

      <Row marginTop={SPACING.spacing20} marginLeft="165px">
        <Col>
          <Span text="Caixa Departamental" />
          <CheckBox
            checked={form.currentCaixa === TipoCaixa.Departamental}
            name="caixaDepartamental"
            onChange={handleSelectCheckbox}
          />
        </Col>
        <InputMask
          marginTop={SPACING.spacing20}
          marginLeft="-5.75rem"
          value={form.caixaDepartamental.numeroCaixa}
          disabled={form.currentCaixa == TipoCaixa.Externa}
          formValue="caixaDepartamental.numeroCaixa"
          name="caixaDepartamental.numeroCaixa"
          onChange={handleOnChange}
          type="number"
          maxLength={8}
          onBlur={
            caixa?.departamental?.nuCaixa + 1 <
              Number(form.caixaDepartamental.numeroCaixa) ||
              caixa?.departamental?.nuCaixa >
              Number(form.caixaDepartamental.numeroCaixa)
              ? gerarCaixaDepartamental
              : null
          }
        />
        <Button
          text="GERAR CAIXA"
          marginLeft={SPACING.spacing10}
          marginTop={SPACING.spacing10}
          disabled={form.currentCaixa == TipoCaixa.Externa}
          onClick={gerarCaixaDepartamental}
        />
      </Row>

      <Grid
        gridCols="4fr 4fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`${SPACING.spacing50} 0 ${SPACING.spacing30} 0`}
      >
        <Input
          label="Descrição do Conteúdo da caixa"
          placeholder="Descrição"
          name="descricao"
          formValue="descricao"
          value={form.descricao}
          onChange={handleOnChange}
          maxLength={200}
        />
      </Grid>

      <Row marginBottom={SPACING.spacing15} justifyContent="flex-end">
        <Button text="Limpar" variantButton="text" onClick={clearForm} />
        <Button
          text="Voltar"
          marginRight="7px"
          variantButton="outlined"
          onClick={() => handleChange(null, 0)}
        />
        <Button text="Salvar" onClick={onSubmit} />
      </Row>
    </>
  )
}

export default AddNewDocumentation
